import { useState, useCallback } from 'react';
import { DataTable, TextInput, Button } from '@etherisc/ui-kit';
import { 
  SearchIcon,
  Filter,
  RefreshCw,
  Eye,
  Download
} from 'lucide-react';
import { createSmartPaginationConfig } from '../../utils/tableHelpers';
import { 
  textColumn,
  statusColumn,
  dateColumn,
  actionsColumn
} from '../../utils/columns';
import type { ActionDefinition } from '../../utils/tableHelpers';
import type { 
  CoverageItem,
  CoverageFilters,
  CoverageStatus,
  CoverageType
} from '../../types/coverage';

interface CoverageListViewProps {
  data: CoverageItem[];
  loading?: boolean;
  error?: string | null;
  filters?: CoverageFilters;
  onFiltersChange?: (filters: Partial<CoverageFilters>) => void;
  onViewDetails?: (item: CoverageItem) => void;
  onRefresh?: () => void;
  onExport?: (selectedItems: CoverageItem[]) => void;
  className?: string;
}

export function CoverageListView({
  data,
  loading = false,
  error = null,
  filters = {},
  onFiltersChange,
  onViewDetails,
  onRefresh,
  onExport,
  className = ''
}: CoverageListViewProps) {
  const [searchQuery, setSearchQuery] = useState(filters.search || '');
  const [showFilters, setShowFilters] = useState(false);

  // Smart pagination configuration for coverage data (standard complexity)
  const paginationSetup = createSmartPaginationConfig(data.length, 'STANDARD');

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    if (onFiltersChange) {
      onFiltersChange({ search: query });
    }
  }, [onFiltersChange]);

  const handleViewDetails = (item: CoverageItem) => {
    if (onViewDetails) {
      onViewDetails(item);
    }
  };

  const handleExport = (item: CoverageItem) => {
    if (onExport) {
      onExport([item]);
    }
  };

  // Define action definitions for the table
  const actionDefinitions: ActionDefinition<CoverageItem>[] = [
    {
      id: 'view',
      label: 'View Details',
      icon: <Eye size={16} />,
      onClick: handleViewDetails,
      intent: 'outline',
    },
    {
      id: 'export',
      label: 'Export Data',
      icon: <Download size={16} />,
      onClick: handleExport,
      intent: 'ghost',
    },
  ];

  // Column definitions using established patterns
  const columns = [
    // Name column
    textColumn<CoverageItem>('name', {
      header: 'Coverage Name',
      getValue: (row: unknown) => (row as CoverageItem).name,
    }),

    // Coverage type 
    statusColumn<CoverageItem>('coverage_type', {
      header: 'Type',
      getValue: (row: unknown) => (row as CoverageItem).coverage_type,
      transformLabel: (value) => {
        const type = value as CoverageType;
        return type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
      },
    }),

    // Data source
    textColumn<CoverageItem>('data_source', {
      header: 'Data Source',
      getValue: (row: unknown) => (row as CoverageItem).data_source,
    }),

    // Region 
    textColumn<CoverageItem>('region_name', {
      header: 'Region',
      getValue: (row: unknown) => {
        const item = row as CoverageItem;
        return item.region_name || item.region_id;
      },
    }),

    // Status with color coding
    statusColumn<CoverageItem>('status', {
      header: 'Status',
      getValue: (row: unknown) => (row as CoverageItem).status,
      transformLabel: (value) => {
        const status = value as CoverageStatus;
        return status.charAt(0).toUpperCase() + status.slice(1);
      },
    }),

    // Variable being measured
    textColumn<CoverageItem>('variable', {
      header: 'Variable',
      getValue: (row: unknown) => (row as CoverageItem).variable,
    }),

    // Date range
    textColumn<CoverageItem>('start_date', {
      header: 'Date Range',
      getValue: (row: unknown) => {
        const item = row as CoverageItem;
        const start = new Date(item.start_date).toLocaleDateString();
        const end = item.end_date ? new Date(item.end_date).toLocaleDateString() : 'Ongoing';
        return `${start} - ${end}`;
      },
      sortable: false,
    }),

    // Created date
    dateColumn<CoverageItem>('created_at', {
      header: 'Created',
      format: 'relative'
    }),

    // Actions column
    actionsColumn<CoverageItem>(actionDefinitions, {
      size: 'sm'
    }),
  ];

  if (error) {
    return (
      <div className={`p-6 ${className}`}>
        <div className="alert alert-error">
          <span>Error loading coverage data: {error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Coverage Data</h2>
          <p className="text-gray-600 mt-1">
            Browse and manage climate coverage data entries
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onRefresh && (
            <Button
              onClick={onRefresh}
              disabled={loading}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          )}
        </div>
      </div>

      {/* Statistics */}
      <CoverageStats data={data} />

      {/* Search and Filters */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
            <div className="pl-10">
              <TextInput
                placeholder="Search coverage data..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
            {Object.keys(filters).length > 1 && (
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                {Object.keys(filters).length - 1}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <CoverageFiltersPanel
          filters={filters}
          onFiltersChange={onFiltersChange}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow">
        <DataTable
          data={data}
          columns={columns}
          enableSorting={true}
          enableResizing={true}
          pagination={paginationSetup.config}
          loading={loading}
        />
      </div>
    </div>
  );
}

// Coverage Statistics Component
interface CoverageStatsProps {
  data: CoverageItem[];
}

function CoverageStats({ data }: CoverageStatsProps) {
  const stats = {
    total: data.length,
    active: data.filter(item => item.status === 'active').length,
    inactive: data.filter(item => item.status === 'inactive').length,
    pending: data.filter(item => item.status === 'pending').length,
    expired: data.filter(item => item.status === 'payment_collection_expired').length,
  };

  const statCards = [
    { label: 'Total', value: stats.total, color: 'text-blue-600' },
    { label: 'Active', value: stats.active, color: 'text-green-600' },
    { label: 'Inactive', value: stats.inactive, color: 'text-gray-600' },
    { label: 'Pending', value: stats.pending, color: 'text-yellow-600' },
    { label: 'Expired', value: stats.expired, color: 'text-red-600' },
  ];

  return (
    <div className="grid grid-cols-5 gap-4">
      {statCards.map((stat) => (
        <div key={stat.label} className="bg-white p-4 rounded-lg border border-gray-200">
          <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
          <div className="text-sm text-gray-500">{stat.label}</div>
        </div>
      ))}
    </div>
  );
}

// Coverage Filters Panel Component
interface CoverageFiltersPanelProps {
  filters: CoverageFilters;
  onFiltersChange?: (filters: Partial<CoverageFilters>) => void;
  onClose: () => void;
}

function CoverageFiltersPanel({ filters, onFiltersChange, onClose }: CoverageFiltersPanelProps) {
  const handleStatusFilter = (status: CoverageStatus, checked: boolean) => {
    if (!onFiltersChange) return;
    
    const currentStatuses = filters.status || [];
    const newStatuses = checked
      ? [...currentStatuses, status]
      : currentStatuses.filter(s => s !== status);
    
    onFiltersChange({ status: newStatuses });
  };

  const handleTypeFilter = (type: CoverageType, checked: boolean) => {
    if (!onFiltersChange) return;
    
    const currentTypes = filters.coverageType || [];
    const newTypes = checked
      ? [...currentTypes, type]
      : currentTypes.filter(t => t !== type);
    
    onFiltersChange({ coverageType: newTypes });
  };

  const clearFilters = () => {
    if (onFiltersChange) {
      onFiltersChange({
        status: [],
        coverageType: [],
        dataSource: [],
        regionId: [],
        dateRange: undefined,
      });
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Filters</h3>
        <div className="flex items-center gap-2">
          <Button onClick={clearFilters} variant="ghost" size="sm">
            Clear All
          </Button>
          <Button onClick={onClose} variant="outline" size="sm">
            Done
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <div className="space-y-2">
            {(['active', 'inactive', 'pending', 'payment_collection_expired'] as CoverageStatus[]).map((status) => (
              <label key={status} className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.status?.includes(status) || false}
                  onChange={(e) => handleStatusFilter(status, e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 mr-2"
                />
                <span className="text-sm capitalize">{status}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Coverage Type Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Coverage Type</label>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {(['temperature', 'precipitation', 'humidity', 'wind_speed', 'pressure', 'radiation'] as CoverageType[]).map((type) => (
              <label key={type} className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.coverageType?.includes(type) || false}
                  onChange={(e) => handleTypeFilter(type, e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 mr-2"
                />
                <span className="text-sm capitalize">{type.replace('_', ' ')}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Date Range Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
          <div className="space-y-2">
            <TextInput
              type="date"
              placeholder="Start Date"
              value={filters.dateRange?.start || ''}
              onChange={(e) => onFiltersChange?.({ 
                dateRange: { ...filters.dateRange, start: e.target.value }
              })}
            />
            <TextInput
              type="date"
              placeholder="End Date"
              value={filters.dateRange?.end || ''}
              onChange={(e) => onFiltersChange?.({ 
                dateRange: { ...filters.dateRange, end: e.target.value }
              })}
            />
          </div>
        </div>

        {/* Data Source Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Data Source</label>
          <TextInput
            placeholder="Filter by data source..."
            value={filters.dataSource?.join(', ') || ''}
            onChange={(e) => {
              const sources = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
              onFiltersChange?.({ dataSource: sources });
            }}
          />
          <p className="text-xs text-gray-500 mt-1">Separate multiple sources with commas</p>
        </div>
      </div>
    </div>
  );
} 